import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Users, Link as LinkIcon, LogOut, BarChart, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import './Dashboard.css';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Link form
  const [linkForm, setLinkForm] = useState({
    influencer_id: '',
    brand_id: '',
    notes: ''
  });

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'linking') {
      fetchInfluencersAndBrands();
      fetchLinks();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInfluencersAndBrands = async () => {
    try {
      const [infResponse, brandResponse] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users?role=influencer`),
        axios.get(`${API_URL}/api/admin/users?role=brand`)
      ]);
      
      // Get influencer profiles
      const infProfiles = await axios.get(`${API_URL}/api/influencers/public?limit=100`);
      setInfluencers(infProfiles.data);
      setBrands(brandResponse.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/links`);
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Failed to load links');
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    
    if (!linkForm.influencer_id || !linkForm.brand_id) {
      toast.error('Please select both influencer and brand');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/admin/link`, linkForm);
      toast.success('Link created successfully!');
      setLinkForm({ influencer_id: '', brand_id: '', notes: '' });
      fetchLinks();
    } catch (error) {
      console.error('Error creating link:', error);
      toast.error(error.response?.data?.detail || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to revoke this link?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/link/${linkId}`);
      toast.success('Link revoked successfully');
      fetchLinks();
    } catch (error) {
      console.error('Error revoking link:', error);
      toast.error('Failed to revoke link');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <h2>Admin Dashboard</h2>
          <p>{user?.name}</p>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
            data-testid="stats-tab"
          >
            <BarChart size={20} />
            <span>Statistics</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="users-tab"
          >
            <Users size={20} />
            <span>Users</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'linking' ? 'active' : ''}`}
            onClick={() => setActiveTab('linking')}
            data-testid="linking-tab"
          >
            <LinkIcon size={20} />
            <span>Linking Tool</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <Button onClick={() => navigate('/')} variant="ghost" className="nav-btn">
            Back to Home
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="nav-btn logout-btn">
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'stats' && (
          <div className="dashboard-content">
            <h1 className="content-title">Platform Statistics</h1>
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{stats.total_users}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.total_influencers}</div>
                  <div className="stat-label">Influencers</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.total_brands}</div>
                  <div className="stat-label">Brands</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.active_links}</div>
                  <div className="stat-label">Active Links</div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="dashboard-content">
            <h1 className="content-title">User Management</h1>
            {loading ? (
              <div className="loading-state">
                <div className="loader"></div>
              </div>
            ) : (
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                        <td><span className={`status-badge ${u.status}`}>{u.status}</span></td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'linking' && (
          <div className="dashboard-content">
            <h1 className="content-title">Linking Tool</h1>
            
            {/* Create Link Form */}
            <div className="link-form-section">
              <h2 className="section-subtitle">Create New Link</h2>
              <form onSubmit={handleCreateLink} className="link-form">
                <div className="form-group">
                  <Label htmlFor="influencer">Select Influencer</Label>
                  <Select
                    value={linkForm.influencer_id}
                    onValueChange={(value) => setLinkForm({...linkForm, influencer_id: value})}
                  >
                    <SelectTrigger data-testid="influencer-select">
                      <SelectValue placeholder="Choose influencer" />
                    </SelectTrigger>
                    <SelectContent>
                      {influencers.map((inf) => (
                        <SelectItem key={inf.id} value={inf.id}>
                          {inf.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="brand">Select Brand</Label>
                  <Select
                    value={linkForm.brand_id}
                    onValueChange={(value) => setLinkForm({...linkForm, brand_id: value})}
                  >
                    <SelectTrigger data-testid="brand-select">
                      <SelectValue placeholder="Choose brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={linkForm.notes}
                    onChange={(e) => setLinkForm({...linkForm, notes: e.target.value})}
                    placeholder="Add any notes about this connection..."
                    rows={3}
                    data-testid="notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="create-link-btn"
                  data-testid="create-link-btn"
                >
                  <LinkIcon size={18} />
                  {loading ? 'Creating...' : 'Create Link'}
                </Button>
              </form>
            </div>

            {/* Active Links Table */}
            <div className="links-section">
              <h2 className="section-subtitle">Active Links</h2>
              {links.length === 0 ? (
                <div className="empty-state">
                  <LinkIcon size={48} />
                  <p>No active links</p>
                </div>
              ) : (
                <div className="links-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Influencer</th>
                        <th>Brand</th>
                        <th>Date Linked</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map((link) => (
                        <tr key={link.id}>
                          <td>{link.influencer_name}</td>
                          <td>{link.brand_name}</td>
                          <td>{new Date(link.created_at).toLocaleDateString()}</td>
                          <td>{link.notes || '-'}</td>
                          <td>
                            <Button
                              onClick={() => handleRevokeLink(link.id)}
                              variant="ghost"
                              className="revoke-btn"
                              data-testid={`revoke-${link.id}`}
                            >
                              <Trash2 size={16} />
                              Revoke
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;